import { TimelineEntry, TimelinePartyType, TimelineStatus } from '~/app/pages/Transfers/_components/Timeline';
import { TransferAdminParty } from '~/app/pages/Transfers/_components/Timeline/types';
import { buildTransferParty } from '~/app/pages/Transfers/_utils/timeline';
import { AdminTransfer, Counterparty } from '~/repositories';
import { AccountNumber, BankAccount } from '~/repositories/BankAccountRepository';

const adminEvents = [
  {
    label: 'Created',
    field: 'createdAt',
    status: TimelineStatus.Success,
  },
  {
    label: 'Completed',
    field: 'completedAt',
    status: TimelineStatus.Success,
  },
];

export const buildAdminEvents = ({
  transfer,
  bankAccount,
  accountNumber,
  entityType,
  counterparty,
}: {
  transfer: AdminTransfer;
  bankAccount: BankAccount;
  accountNumber?: AccountNumber;
  entityType?: 'person' | 'business';
  counterparty?: Counterparty;
}): TimelineEntry[] => {
  const isCredit = transfer.type === 'credit';
  const entries: TimelineEntry[] = [];

  adminEvents
    .filter((event) => {
      if (event.field === 'createdAt') {
        return !!transfer[event.field as keyof typeof transfer];
      }
      if (event.field === 'completedAt') {
        const completedAt = transfer[event.field as keyof typeof transfer];
        return !!completedAt || !!transfer.createdAt;
      }
      return !!transfer[event.field as keyof typeof transfer];
    })
    .forEach((event) => {
      const amount = transfer.amount
        ? {
            value: typeof transfer.amount === 'string' ? parseFloat(transfer.amount) : transfer.amount,
            isAdding: event.field === 'completedAt' ? isCredit : isCredit,
            currencyCode: transfer.currency || 'USD',
          }
        : undefined;

      let date: Date | undefined;
      if (event.field === 'completedAt') {
        const completedAt = transfer.completedAt;
        if (completedAt) {
          date = new Date(completedAt);
        } else if (transfer.createdAt) {
          date = new Date(transfer.createdAt);
        }
      } else {
        const fieldValue = transfer[event.field as keyof typeof transfer];
        if (fieldValue) {
          date = new Date(fieldValue as string);
        }
      }

      const shouldShowParty = event.field === 'createdAt' || event.field === 'completedAt';

      if (shouldShowParty) {
        const isCreatedEvent = event.field === 'createdAt';
        const hasDetails =
          transfer.details && typeof transfer.details === 'object' && Object.keys(transfer.details).length > 0;
        let partyData;

        if (isCreatedEvent && !hasDetails) {
          partyData = {
            data: {
              adminName: 'Column National Association',
            } as TransferAdminParty,
            partyType: isCredit ? TimelinePartyType.Sender : TimelinePartyType.Receiver,
          };
        } else if (isCreatedEvent) {
          if (accountNumber) {
            if (isCredit && counterparty) {
              partyData = buildTransferParty({
                account: {
                  accountNumber: accountNumber,
                  bankAccount: bankAccount,
                  entityType: entityType,
                  isBookTransfer: false,
                },
                counterparty: counterparty,
                isIncoming: isCredit,
                isOriginator: true,
              });
            } else if (!isCredit) {
              partyData = buildTransferParty({
                account: {
                  accountNumber: accountNumber,
                  bankAccount: bankAccount,
                  entityType: entityType,
                  isBookTransfer: false,
                },
                isIncoming: isCredit,
                isOriginator: true,
              });
            } else {
              partyData = buildTransferParty({
                account: {
                  accountNumber: accountNumber,
                  bankAccount: bankAccount,
                  entityType: entityType,
                  isBookTransfer: false,
                },
                isIncoming: isCredit,
                isOriginator: false,
              });
            }
          }
        } else {
          if (accountNumber) {
            if (isCredit) {
              partyData = buildTransferParty({
                account: {
                  accountNumber: accountNumber,
                  bankAccount: bankAccount,
                  entityType: entityType,
                  isBookTransfer: false,
                },
                isIncoming: isCredit,
                isOriginator: false,
              });
            } else if (counterparty) {
              partyData = buildTransferParty({
                account: {
                  accountNumber: accountNumber,
                  bankAccount: bankAccount,
                  entityType: entityType,
                  isBookTransfer: false,
                },
                counterparty: counterparty,
                isIncoming: isCredit,
                isOriginator: false,
              });
            } else {
              partyData = buildTransferParty({
                account: {
                  accountNumber: accountNumber,
                  bankAccount: bankAccount,
                  entityType: entityType,
                  isBookTransfer: false,
                },
                isIncoming: isCredit,
                isOriginator: true,
              });
            }
          }
        }

        entries.push({
          label: event.label,
          amount,
          date,
          partyData,
          status: event.status,
        });
      } else {
        entries.push({
          label: event.label,
          amount,
          date,
          status: event.status,
        });
      }
    });

  return entries;
};
